/**
 * Client bodies for `/settings/agents/*` — #44573.
 *
 * Each page self-fetches its seed via `settingsAgentsPages.*` (or an existing
 * contract like `reposList` / `contextPack.ftsStatus`) and writes through the
 * already-mounted mutation contracts. Live Next pages are UNTOUCHED.
 */

import * as React from "react";
import {
  Badge,
  Button,
  CheckboxField,
  DatabaseAssistant,
  FormField,
  Input,
  IntentBadge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SettingsPageShell,
  SettingsRow,
  SettingsSection,
  Switch,
  formatRelativeTime,
  formatUsd,
} from "@excellent/ui";
import { apiCall, useApi } from "@excellent/ui/api";
import { settingsAgentsPages } from "@excellent/ui/api/contracts/settings-agents-pages";
import { projectActions } from "@excellent/ui/api/contracts/project-actions";
import { agentSettings, type AgentCliLinkResult } from "@excellent/ui/api/contracts/agent-settings";
import { otherActions } from "@excellent/ui/api/contracts/other-actions";
import { optimizationSettings } from "@excellent/ui/api/contracts/optimization-settings";
import { verdicts } from "@excellent/ui/api/contracts/verdicts";
import {
  reposList,
  repoConfirm,
  repoArchive,
  type RepoRow,
  type RepoStatus,
} from "@excellent/ui/api/contracts/repos";
import { contextPack } from "@excellent/ui/api/contracts/context-pack";
import { onboardingControls } from "@excellent/ui/api/contracts/onboarding-controls";
import { verificationObservation } from "@excellent/ui/api/contracts/verification-observation";
import { apiKey } from "@excellent/ui/api";
import { useSWRConfig } from "swr";

function Loading() {
  return <p className="type-body text-muted-foreground">Loading…</p>;
}

function ErrorLine({ error }: { error: unknown }) {
  if (!error) return null;
  const msg = error instanceof Error ? error.message : String(error);
  return <p className="type-body text-destructive">{msg}</p>;
}

// ── Repos ───────────────────────────────────────────────────────────────────

function repoLabel(r: RepoRow): { name: string; detail: string } {
  const url = r.remote_url ?? r.display_name;
  if (url.startsWith("local:")) {
    const sha = url.slice("local:".length);
    return {
      name: `Local repo · ${sha.slice(0, 10)}`,
      detail: "No remote — identified by its first commit",
    };
  }
  const clean = url.replace(/\.git$/, "");
  const base = clean.split("/").filter(Boolean).pop();
  return { name: base || clean, detail: clean };
}

function RepoList({
  rows,
  status,
  busy,
  onAction,
}: {
  rows: RepoRow[];
  status: RepoStatus;
  busy: boolean;
  onAction: (id: number, kind: "confirm" | "archive") => Promise<void>;
}) {
  if (rows.length === 0) {
    const empty: Record<RepoStatus, string> = {
      pending:
        "Nothing waiting. A repo lands here the first time you commit in it with the hooks installed (run `excellent-mcp repo init` in a project).",
      active: "No repos are being tracked yet. Confirm a pending repo to start.",
      archived: "Nothing archived.",
    };
    return <div className="type-body text-muted-foreground">{empty[status]}</div>;
  }
  return (
    <ul>
      {rows.map((r) => {
        const { name, detail } = repoLabel(r);
        return (
          <li
            key={r.id}
            className="flex items-center justify-between gap-3 border-b border-border/40 py-3 first:pt-0 last:border-b-0 last:pb-0"
          >
            <div className="min-w-0 flex-1">
              <a
                href={`/settings/repos/${r.id}`}
                className="type-body text-foreground truncate hover:underline"
              >
                {name}
              </a>
              <div className="type-body text-muted-foreground truncate">{detail}</div>
            </div>
            <div className="flex shrink-0 gap-2">
              {status === "pending" ? (
                <Button size="sm" disabled={busy} onClick={() => void onAction(r.id, "confirm")}>
                  Confirm
                </Button>
              ) : null}
              {status === "active" ? (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => void onAction(r.id, "archive")}
                >
                  Archive
                </Button>
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function ReposAgentsPage() {
  const { mutate } = useSWRConfig();
  const { data, isLoading, error } = useApi(reposList, { query: {} });
  const [busy, setBusy] = React.useState(false);

  async function handleAction(id: number, kind: "confirm" | "archive") {
    setBusy(true);
    try {
      const op = kind === "confirm" ? repoConfirm : repoArchive;
      await apiCall(op, { params: { id } });
      await mutate(apiKey(reposList, { query: {} }));
    } finally {
      setBusy(false);
    }
  }

  const rows = data?.repos ?? [];
  const pending = rows.filter((r) => r.status === "pending");
  const active = rows.filter((r) => r.status === "active");
  const archived = rows.filter((r) => r.status === "archived");

  return (
    <SettingsPageShell
      title="Repositories"
      description={
        <span>
          The git repositories Excellent has seen through its{" "}
          <strong>commit-attribution hooks</strong>. <strong>Confirm</strong> a repo to start
          tracking its commits and branches — that&apos;s what feeds the commit panes behind the
          ship/verify gate. <strong>Archive</strong> one to stop tracking it.
        </span>
      }
    >
      <ErrorLine error={error} />
      {isLoading && !data ? (
        <Loading />
      ) : (
        <div className="space-y-6" aria-busy={busy ? "true" : "false"}>
          <SettingsSection title="Pending" count={pending.length}>
            <RepoList rows={pending} status="pending" busy={busy} onAction={handleAction} />
          </SettingsSection>
          <SettingsSection title="Tracked" count={active.length}>
            <RepoList rows={active} status="active" busy={busy} onAction={handleAction} />
          </SettingsSection>
          <SettingsSection title="Archived" count={archived.length}>
            <RepoList rows={archived} status="archived" busy={busy} onAction={handleAction} />
          </SettingsSection>
        </div>
      )}
    </SettingsPageShell>
  );
}

// ── Auto-verify ─────────────────────────────────────────────────────────────

export function AutoVerifyAgentsPage() {
  const { data, isLoading, error, mutate } = useApi(settingsAgentsPages.autoVerify, {});
  const [busy, setBusy] = React.useState(false);

  async function onSave(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!data) return;
    const fd = new FormData(e.currentTarget);
    setBusy(true);
    try {
      await apiCall(projectActions.saveAutoVerifyConfigForm, {
        body: {
          enabled: fd.get("enabled") === "on",
          mode: String(fd.get("mode") || "draft") as "draft" | "autonomous",
          model: String(fd.get("model") || ""),
          max_turns: Number(fd.get("max_turns") || data.max_turns),
          max_runtime_seconds: Number(fd.get("max_runtime_seconds") || data.max_runtime_seconds),
          dangerous_skip_permissions: fd.get("dangerous_skip_permissions") === "on",
          allow_subtasks: fd.get("allow_subtasks") === "on",
          max_subtasks: Number(fd.get("max_subtasks") || data.max_subtasks),
        },
      });
      await mutate();
    } finally {
      setBusy(false);
    }
  }

  return (
    <SettingsPageShell
      title="Auto-verify"
      description={
        <span>
          Per-task button that spawns <code>claude -p</code> server-side to check a task with
          pending evidence. Disabled by default because runs cost real API tokens. The hard limits
          below cannot be exceeded — settings are clamped on save.
        </span>
      }
    >
      <ErrorLine error={error} />
      {isLoading && !data ? (
        <Loading />
      ) : data ? (
        <form onSubmit={onSave} className="space-y-6">
          <SettingsSection title="Checker">
            <div className="flex flex-col gap-4">
              <CheckboxField
                name="enabled"
                defaultChecked={data.enabled}
                label="Enabled"
                description="Show the Auto-verify button on issues awaiting an evidence decision."
              />
              <FormField label="Mode">
                <Select name="mode" defaultValue={data.mode}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">
                      Draft — checker posts a recommendation; you click Apply
                    </SelectItem>
                    <SelectItem value="autonomous">
                      Autonomous — checker records the decision directly
                    </SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Model">
                <Input name="model" defaultValue={data.model} placeholder="claude-sonnet-4-6" />
              </FormField>
              <CheckboxField
                name="dangerous_skip_permissions"
                defaultChecked={data.dangerous_skip_permissions}
                label="--dangerously-skip-permissions"
                description="Pass this flag to claude -p so the verifier doesn't pause for every tool call."
              />
            </div>
          </SettingsSection>
          <SettingsSection title="Limits">
            <div className="flex flex-col gap-4">
              <FormField
                label="Max turns"
                help={`Hard limit: ${data.limits.max_turns.min}–${data.limits.max_turns.max}.`}
              >
                <Input
                  type="number"
                  name="max_turns"
                  defaultValue={data.max_turns}
                  min={data.limits.max_turns.min}
                  max={data.limits.max_turns.max}
                  className="w-32"
                />
              </FormField>
              <FormField
                label="Max runtime (seconds)"
                help={`Hard limit: ${data.limits.max_runtime_seconds.min}–${data.limits.max_runtime_seconds.max}s.`}
              >
                <Input
                  type="number"
                  name="max_runtime_seconds"
                  defaultValue={data.max_runtime_seconds}
                  min={data.limits.max_runtime_seconds.min}
                  max={data.limits.max_runtime_seconds.max}
                  className="w-32"
                />
              </FormField>
              <CheckboxField
                name="allow_subtasks"
                defaultChecked={data.allow_subtasks}
                label="Allow sub-issue creation"
                description="Let the verifier file sub-issues for non-blocking improvements."
              />
              <FormField
                label="Max sub-issues per run"
                help={`Hard limit: ${data.limits.max_subtasks.min}–${data.limits.max_subtasks.max}.`}
              >
                <Input
                  type="number"
                  name="max_subtasks"
                  defaultValue={data.max_subtasks}
                  min={data.limits.max_subtasks.min}
                  max={data.limits.max_subtasks.max}
                  className="w-32"
                />
              </FormField>
            </div>
          </SettingsSection>
          <div className="flex justify-end">
            <Button type="submit" disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </Button>
          </div>
        </form>
      ) : null}
    </SettingsPageShell>
  );
}

// ── Audits ──────────────────────────────────────────────────────────────────

export function AuditsAgentsPage() {
  const { data, isLoading, error, mutate } = useApi(settingsAgentsPages.audits, {});
  const [busy, setBusy] = React.useState<string | null>(null);

  async function saveConfig(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!data) return;
    const fd = new FormData(e.currentTarget);
    setBusy("config");
    try {
      await apiCall(projectActions.saveSectionAuditConfig, {
        body: {
          enabled: fd.get("enabled") === "on",
          cli: String(fd.get("cli") || "claude") as "claude" | "codex",
          model: String(fd.get("model") || ""),
          max_runtime_seconds: Number(fd.get("max_runtime_seconds") || data.max_runtime_seconds),
          default_cadence_days: Number(fd.get("default_cadence_days") || data.default_cadence_days),
          dangerous_skip_permissions: fd.get("dangerous_skip_permissions") === "on",
        },
      });
      await mutate();
    } finally {
      setBusy(null);
    }
  }

  async function saveAutomation(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    setBusy("auto");
    try {
      await apiCall(otherActions.auditSaveAutomation, {
        body: {
          auditReconcile: fd.get("auditReconcile") === "on",
          auditAutorun: fd.get("auditAutorun") === "on",
          auditAutofix: fd.get("auditAutofix") === "on",
        },
      });
      await mutate();
    } finally {
      setBusy(null);
    }
  }

  return (
    <SettingsPageShell
      title="Audits"
      description={
        <span>
          The best-in-class section audits spawn a coding agent (<code>claude</code> or{" "}
          <code>codex</code>) server-side to inspect each business function against what good looks
          like and emit findings.
        </span>
      }
    >
      <ErrorLine error={error} />
      {isLoading && !data ? (
        <Loading />
      ) : data ? (
        <>
          <SettingsSection title="Configuration">
            <form onSubmit={saveConfig} className="flex flex-col gap-4">
              <CheckboxField
                name="enabled"
                label="Enabled"
                description="Allow section audits and fix-with-agent to spawn."
                defaultChecked={data.enabled}
              />
              <FormField label="CLI">
                <Select name="cli" defaultValue={data.cli}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="claude">claude</SelectItem>
                    <SelectItem value="codex">codex</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Model">
                <Input name="model" defaultValue={data.model} />
              </FormField>
              <FormField
                label="Max runtime (seconds)"
                help={`${data.limits.max_runtime_seconds.min}–${data.limits.max_runtime_seconds.max}s`}
              >
                <Input
                  type="number"
                  name="max_runtime_seconds"
                  defaultValue={data.max_runtime_seconds}
                  className="w-32"
                />
              </FormField>
              <FormField
                label="Default cadence (days)"
                help={`${data.limits.default_cadence_days.min}–${data.limits.default_cadence_days.max}`}
              >
                <Input
                  type="number"
                  name="default_cadence_days"
                  defaultValue={data.default_cadence_days}
                  className="w-32"
                />
              </FormField>
              <CheckboxField
                name="dangerous_skip_permissions"
                label="--dangerously-skip-permissions"
                defaultChecked={data.dangerous_skip_permissions}
              />
              <div className="flex justify-end">
                <Button type="submit" disabled={busy === "config"}>
                  Save
                </Button>
              </div>
            </form>
          </SettingsSection>
          <SettingsSection title="Automation">
            <form onSubmit={saveAutomation} className="flex flex-col gap-4">
              <CheckboxField
                name="auditReconcile"
                label="Surface due audits (recommended)"
                defaultChecked={data.automation.reconcile}
              />
              <CheckboxField
                name="auditAutorun"
                label="Run due audits automatically"
                defaultChecked={data.automation.autorun}
              />
              <CheckboxField
                name="auditAutofix"
                label="Auto-open fixes for high-severity findings"
                defaultChecked={data.automation.autofix}
              />
              <div className="flex justify-end">
                <Button type="submit" disabled={busy === "auto"}>
                  Save
                </Button>
              </div>
            </form>
          </SettingsSection>
        </>
      ) : null}
    </SettingsPageShell>
  );
}

// ── Simple toggle-style pages (mirror / fleet / copilot / consent) ──

export function MirrorAgentsPage() {
  const { data, isLoading, error, mutate } = useApi(settingsAgentsPages.mirror, {});
  const [on, setOn] = React.useState(false);
  React.useEffect(() => {
    if (data) setOn(data.enabled);
  }, [data]);

  async function onSave(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    await apiCall(agentSettings.mirrorSaveSettings, {
      body: {
        enabled: on,
        remote: String(fd.get("remote") || "origin"),
        branch: String(fd.get("branch") || "main"),
      },
    });
    await mutate();
  }

  return (
    <SettingsPageShell
      title="Repo mirror"
      description={
        <span>
          Auto-push this machine&apos;s local branch to its git remote so upstream never drifts
          behind the work the fleet lands locally. <strong>Fast-forward only</strong>. Off by
          default.
        </span>
      }
    >
      <ErrorLine error={error} />
      {isLoading && !data ? (
        <Loading />
      ) : data ? (
        <form onSubmit={onSave} className="flex flex-col gap-6">
          <SettingsSection title="Auto-push">
            <SettingsRow
              label="Enable auto-push"
              description="Mirrors local → remote each tick. Never force-pushes."
            >
              <Switch checked={on} onCheckedChange={setOn} aria-label="Enable auto-push" />
            </SettingsRow>
          </SettingsSection>
          <SettingsSection title="Target">
            <SettingsRow label="Remote">
              <Input name="remote" defaultValue={data.remote} className="w-64" />
            </SettingsRow>
            <SettingsRow label="Branch">
              <Input name="branch" defaultValue={data.branch} className="w-64" />
            </SettingsRow>
          </SettingsSection>
          <div>
            <Button type="submit">Save</Button>
          </div>
        </form>
      ) : null}
    </SettingsPageShell>
  );
}

export function FleetAgentsPage() {
  const { data, isLoading, error, mutate } = useApi(settingsAgentsPages.fleet, {});
  const [enabled, setEnabled] = React.useState(false);
  const [poolSize, setPoolSize] = React.useState("2");
  React.useEffect(() => {
    if (data) {
      setEnabled(data.enabled);
      setPoolSize(String(data.poolSize));
    }
  }, [data]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    await apiCall(agentSettings.fleetSaveSettings, {
      body: { enabled, poolSize: Number(poolSize) || data?.defaultPool || 2 },
    });
    await mutate();
  }

  return (
    <SettingsPageShell
      title="Fleet"
      description={
        <span>
          How many agents run at once on this machine, and whether they&apos;re isolated. Watch live
          runs from{" "}
          <a className="underline" href="/?env=real">
            This Mac
          </a>
          .
        </span>
      }
    >
      <ErrorLine error={error} />
      {isLoading && !data ? (
        <Loading />
      ) : data ? (
        <form onSubmit={save} className="flex flex-col gap-6">
          <SettingsSection title="Worktrees">
            <SettingsRow
              label="Enable worktree isolation"
              description={
                data.enabledEnvPinned
                  ? "Pinned by environment."
                  : "Each concurrent worker leases a git worktree."
              }
              control={
                <Switch
                  checked={enabled}
                  onCheckedChange={setEnabled}
                  disabled={data.enabledEnvPinned}
                  aria-label="Enable worktree isolation"
                />
              }
            />
            <SettingsRow
              label="Pool size"
              description={`Default ${data.defaultPool}; range ${data.min}–${data.max}.`}
            >
              <Input
                type="number"
                value={poolSize}
                onChange={(e) => setPoolSize(e.target.value)}
                min={data.min}
                max={data.max}
                disabled={data.poolSizeEnvPinned}
                className="w-24"
              />
            </SettingsRow>
          </SettingsSection>
          <div>
            <Button type="submit">Save</Button>
          </div>
        </form>
      ) : null}
    </SettingsPageShell>
  );
}

export function CopilotAgentsPage() {
  const { data, isLoading, error, mutate } = useApi(settingsAgentsPages.copilot, {});
  const [enabled, setEnabled] = React.useState(false);
  const [develop, setDevelop] = React.useState(false);
  const [warm, setWarm] = React.useState(false);
  const [autonomy, setAutonomy] = React.useState<"ask" | "auto">("auto");
  React.useEffect(() => {
    if (data) {
      setEnabled(data.enabled);
      setDevelop(data.develop);
      setWarm(data.warmSessions);
      setAutonomy(data.autonomy);
    }
  }, [data]);

  async function save() {
    await apiCall(otherActions.copilotSaveSettings, {
      body: { enabled, develop, autonomy, warmSessions: warm },
    });
    await mutate();
  }

  return (
    <SettingsPageShell
      title="Chat"
      description={
        <span>
          The in-shell <strong>Chat</strong> agent — a real Claude instance in the activity rail
          that can <em>operate</em> your workspace. Off by default.
        </span>
      }
    >
      <ErrorLine error={error} />
      {isLoading && !data ? (
        <Loading />
      ) : data ? (
        <div className="flex flex-col gap-6">
          <SettingsSection title="Chat agent">
            <SettingsRow
              label="Enable Chat"
              control={
                <Switch checked={enabled} onCheckedChange={setEnabled} aria-label="Enable Chat" />
              }
            />
            <SettingsRow
              label="Develop"
              description="Grants fs/Bash tools so Chat can edit this app."
              control={
                <Switch checked={develop} onCheckedChange={setDevelop} aria-label="Develop" />
              }
            />
            <SettingsRow
              label="Warm sessions"
              description="Keep a long-lived process per conversation (experimental)."
              control={
                <Switch checked={warm} onCheckedChange={setWarm} aria-label="Warm sessions" />
              }
            />
            <SettingsRow label="Autonomy">
              <Select value={autonomy} onValueChange={(v) => setAutonomy(v as "ask" | "auto")}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">auto</SelectItem>
                  <SelectItem value="ask">ask</SelectItem>
                </SelectContent>
              </Select>
            </SettingsRow>
          </SettingsSection>
          <div>
            <Button onClick={() => void save()}>Save</Button>
          </div>
        </div>
      ) : null}
    </SettingsPageShell>
  );
}

export function ConsentAgentsPage() {
  const { data, isLoading, error, mutate } = useApi(settingsAgentsPages.consent, {});
  const [enabled, setEnabled] = React.useState(false);
  const [autonomy, setAutonomy] = React.useState<"ask" | "auto">("ask");
  React.useEffect(() => {
    if (data) {
      setEnabled(data.enabled);
      setAutonomy(data.autonomy);
    }
  }, [data]);

  async function save() {
    await apiCall(otherActions.browserActEnabled, { body: { enabled } });
    await apiCall(otherActions.browserActAutonomy, { body: { autonomy } });
    await mutate();
  }

  return (
    <SettingsPageShell
      title="Browser actions"
      description={
        <span>
          Let an agent <strong>act</strong> in your real, signed-in browser.{" "}
          <strong>Off by default.</strong>
        </span>
      }
    >
      <ErrorLine error={error} />
      {isLoading && !data ? (
        <Loading />
      ) : data ? (
        <div className="flex flex-col gap-6">
          <SettingsSection title="Permission">
            <SettingsRow
              label="Allow browser actions"
              control={
                <Switch
                  checked={enabled}
                  onCheckedChange={setEnabled}
                  aria-label="Allow browser actions"
                />
              }
            />
            <SettingsRow label="Autonomy">
              <Select value={autonomy} onValueChange={(v) => setAutonomy(v as "ask" | "auto")}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ask">ask</SelectItem>
                  <SelectItem value="auto">auto</SelectItem>
                </SelectContent>
              </Select>
            </SettingsRow>
          </SettingsSection>
          <SettingsSection title="Sites" count={data.sites.length}>
            {data.sites.length === 0 ? (
              <p className="type-body text-muted-foreground">No site consents yet.</p>
            ) : (
              <ul>
                {data.sites.map((s) => (
                  <li
                    key={s.origin}
                    className="flex items-center justify-between border-b border-border/40 py-2"
                  >
                    <span className="type-body">{s.origin}</span>
                    <Badge variant="neutral">{s.label ?? "allowed"}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </SettingsSection>
          <div>
            <Button onClick={() => void save()}>Save</Button>
          </div>
        </div>
      ) : null}
    </SettingsPageShell>
  );
}

// ── Remaining pages ─────────────────────────────────────────────────────────

export function DatabaseAgentsPage() {
  return (
    <SettingsPageShell
      title="Database assistant"
      description="Chat with your linked model about the local database — it proposes SQL through the audited, read-only-by-default runner, and you review and run it."
    >
      <DatabaseAssistant mode="page" />
    </SettingsPageShell>
  );
}

export function CredentialsAgentsPage() {
  const { data, isLoading, error } = useApi(settingsAgentsPages.credentials, {});
  const observation = useApi(verificationObservation.status, {});
  const [revokingGrantId, setRevokingGrantId] = React.useState<string | null>(null);
  async function revokeObservationGrant(grantId: string) {
    setRevokingGrantId(grantId);
    try {
      await apiCall(verificationObservation.revoke, { body: { grantId } });
      await observation.mutate();
    } finally {
      setRevokingGrantId(null);
    }
  }
  const activeGrants = observation.data?.activeGrants ?? [];
  return (
    <SettingsPageShell
      title="Credentials"
      description={
        <span>
          Where each provider&apos;s usage is billed, made visible — the anti-Hermes-bug surface.
          Read-only.
        </span>
      }
    >
      <ErrorLine error={error} />
      {isLoading && !data ? (
        <Loading />
      ) : (
        <SettingsSection title="Provenance" count={data?.rows.length ?? 0}>
          {!data?.rows.length ? (
            <p className="type-body text-muted-foreground">No credential provenance rows.</p>
          ) : (
            <ul>
              {data.rows.map((r) => (
                <li
                  key={r.provider}
                  className="flex items-center justify-between border-b border-border/40 py-2"
                >
                  <span className="type-body font-medium">{r.label ?? r.provider}</span>
                  <span className="type-subtext text-muted-foreground">{r.provenance}</span>
                </li>
              ))}
            </ul>
          )}
        </SettingsSection>
      )}
      <SettingsSection title="Observation consent" count={activeGrants.length}>
        {activeGrants.length === 0 ? (
          <p className="text-sm text-muted-foreground">No active observation grants</p>
        ) : (
          <ul>
            {activeGrants.map((grant) => (
              <li
                key={grant.id}
                className="flex items-center justify-between border-b border-border/40 py-2"
              >
                <span className="text-sm font-medium">{grant.source}</span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={revokingGrantId !== null}
                  onClick={() => void revokeObservationGrant(grant.id)}
                >
                  {revokingGrantId === grant.id ? "Revoking…" : "Revoke"}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </SettingsSection>
    </SettingsPageShell>
  );
}

export function ResidencyAgentsPage() {
  const { data, isLoading, error } = useApi(settingsAgentsPages.residency, {});
  return (
    <SettingsPageShell
      title="Residency disclosure"
      description="What runs where. Derived live from the provider registry — this page IS the policy, not a copy of it."
    >
      <ErrorLine error={error} />
      {isLoading && !data ? (
        <Loading />
      ) : data ? (
        <div className="flex flex-col gap-8">
          <SettingsSection title="Deployment posture">
            <IntentBadge intent={data.sovereign ? "success" : "info"}>
              {data.sovereign ? "Sovereign — air-gapped" : "Standard"}
            </IntentBadge>
          </SettingsSection>
          <SettingsSection title="Tier 1 — never leaves this machine">
            {data.local.length ? (
              data.local.map((p) => (
                <div key={p.id} className="flex items-center gap-3 border-b border-border/40 py-2">
                  <span className="flex-1 type-body">{p.id}</span>
                  <IntentBadge intent="success">loopback only</IntentBadge>
                </div>
              ))
            ) : (
              <p className="type-body text-muted-foreground">No local runtime detected yet.</p>
            )}
          </SettingsSection>
          <SettingsSection title="Tier 2 — leaves only with configuration">
            {data.remote.length ? (
              data.remote.map((p) => (
                <div key={p.id} className="flex items-center gap-3 border-b border-border/40 py-2">
                  <span className="flex-1 type-body">{p.id}</span>
                  {p.consented ? (
                    <IntentBadge intent="warning">consented escalation</IntentBadge>
                  ) : null}
                  <IntentBadge intent={p.dataPolicy === "zdr" ? "success" : "neutral"}>
                    {p.dataPolicy ?? "default"}
                  </IntentBadge>
                </div>
              ))
            ) : (
              <p className="type-body text-muted-foreground">None.</p>
            )}
          </SettingsSection>
          <SettingsSection title="Tier 3 — leaves only with explicit consent">
            {data.consented.length ? (
              <p className="type-body">Granted for: {data.consented.join(", ")}</p>
            ) : (
              <p className="type-body text-muted-foreground">No escalation consents granted.</p>
            )}
          </SettingsSection>
        </div>
      ) : null}
    </SettingsPageShell>
  );
}

export function ContextAgentsPage() {
  const { data, isLoading, error, mutate } = useApi(contextPack.ftsStatus, {});
  const [budget, setBudget] = React.useState(4000);
  React.useEffect(() => {
    if (data) setBudget(data.tokenBudget);
  }, [data]);

  async function save() {
    await apiCall(contextPack.saveConfig, { body: { tokenBudget: budget } });
    await mutate();
  }
  async function rebuild() {
    await apiCall(contextPack.ftsRebuild, {});
    await mutate();
  }

  return (
    <SettingsPageShell
      title="Context"
      description="The retrieval index and budget behind the context packs your agents assemble."
    >
      <ErrorLine error={error} />
      {isLoading && !data ? (
        <Loading />
      ) : data ? (
        <div className="flex flex-col gap-6">
          <SettingsSection title="Retrieval">
            <div className="flex items-center justify-between">
              <span className="type-body">{data.indexed} documents indexed</span>
              <Button variant="outline" onClick={() => void rebuild()}>
                Rebuild index
              </Button>
            </div>
          </SettingsSection>
          <SettingsSection title="Budget">
            <FormField label="Token budget">
              <Input
                type="number"
                value={budget}
                onChange={(e) => setBudget(Number(e.target.value))}
                className="w-32"
              />
            </FormField>
            <div className="mt-4">
              <Button onClick={() => void save()}>Save</Button>
            </div>
          </SettingsSection>
        </div>
      ) : null}
    </SettingsPageShell>
  );
}

export function NotifyAgentsPage() {
  const { data, isLoading, error, mutate } = useApi(settingsAgentsPages.notify, {});
  const [enabled, setEnabled] = React.useState(false);
  React.useEffect(() => {
    if (data) setEnabled(data.enabled);
  }, [data]);

  async function onSave(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!data) return;
    const fd = new FormData(e.currentTarget);
    await apiCall(agentSettings.notifySaveSettings, {
      body: {
        enabled,
        provider: String(fd.get("provider") || data.provider) as
          "log" | "resend" | "telegram" | "http",
        from: String(fd.get("from") || data.from || ""),
        events: {
          reviewQueue: fd.get("reviewQueue") === "on",
          promoteReady: fd.get("promoteReady") === "on",
          autopilotStall: fd.get("autopilotStall") === "on",
          auditHigh: fd.get("auditHigh") === "on",
          inboundAck: fd.get("inboundAck") === "on",
        },
        digest: String(fd.get("digest") || data.digest || "instant") as "instant" | "daily",
      },
    });
    const recipient = String(fd.get("recipient") || "");
    if (recipient) {
      await apiCall(agentSettings.notifySaveRecipient, { body: { value: recipient } });
    }
    await mutate();
  }

  return (
    <SettingsPageShell
      title="Notify"
      description={
        <span>
          Push a human-gated-work signal off this machine over email, Telegram, or a webhook.{" "}
          <strong>Off by default.</strong>
        </span>
      }
    >
      <ErrorLine error={error} />
      {isLoading && !data ? (
        <Loading />
      ) : data ? (
        <form onSubmit={onSave} className="flex flex-col gap-6">
          <SettingsSection title="Channel">
            <SettingsRow
              label="Enable notify"
              control={
                <Switch checked={enabled} onCheckedChange={setEnabled} aria-label="Enable notify" />
              }
            />
            <FormField label="Provider">
              <Select name="provider" defaultValue={data.provider}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="log">log</SelectItem>
                  <SelectItem value="resend">resend</SelectItem>
                  <SelectItem value="telegram">telegram</SelectItem>
                  <SelectItem value="http">http</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Recipient">
              <Input name="recipient" defaultValue={data.recipient ?? ""} className="max-w-md" />
            </FormField>
            <FormField label="From">
              <Input name="from" defaultValue={data.from ?? ""} className="max-w-md" />
            </FormField>
          </SettingsSection>
          <SettingsSection title="Events">
            {(
              [
                ["reviewQueue", "Inbox"],
                ["promoteReady", "Promote-ready goal"],
                ["autopilotStall", "Autopilot stall"],
                ["auditHigh", "High-severity audit"],
                ["inboundAck", "Inbound ack"],
              ] as const
            ).map(([key, label]) => (
              <CheckboxField
                key={key}
                name={key}
                label={label}
                defaultChecked={Boolean((data.events as Record<string, boolean>)[key])}
              />
            ))}
          </SettingsSection>
          <div>
            <Button type="submit">Save</Button>
          </div>
        </form>
      ) : null}
    </SettingsPageShell>
  );
}

export function InboundAgentsPage() {
  const { data, isLoading, error, mutate } = useApi(settingsAgentsPages.inbound, {});
  const [enabled, setEnabled] = React.useState(false);
  React.useEffect(() => {
    if (data) setEnabled(data.enabled);
  }, [data]);

  async function save(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    await apiCall(agentSettings.inboundSaveSettings, {
      body: {
        enabled,
        rateLimitPerHour: Number(fd.get("rateLimitPerHour") || data?.rateLimitPerHour || 5),
      },
    });
    await mutate();
  }

  async function mint() {
    await apiCall(agentSettings.inboundMintPairing, {});
    await mutate();
  }

  return (
    <SettingsPageShell
      title="Inbound"
      description={
        <span>
          Let an allowlisted Telegram chat spawn a gated agent session by texting your fleet.{" "}
          <strong>Off by default.</strong>
        </span>
      }
    >
      <ErrorLine error={error} />
      {isLoading && !data ? (
        <Loading />
      ) : data ? (
        <form onSubmit={save} className="flex flex-col gap-6">
          <SettingsSection title="Telegram inbound">
            <SettingsRow
              label="Enable inbound"
              control={
                <Switch
                  checked={enabled}
                  onCheckedChange={setEnabled}
                  aria-label="Enable inbound"
                />
              }
            />
            <FormField label="Rate limit per hour">
              <Input
                type="number"
                name="rateLimitPerHour"
                defaultValue={data.rateLimitPerHour ?? 5}
                className="w-32"
              />
            </FormField>
            {data.pending?.code ? (
              <p className="type-body">
                Pairing code <code>{data.pending.code}</code>
                {data.pending.expiresAt ? ` · expires ${data.pending.expiresAt}` : ""}
              </p>
            ) : null}
          </SettingsSection>
          <div className="flex gap-2">
            <Button type="submit">Save</Button>
            <Button type="button" variant="outline" onClick={() => void mint()}>
              Mint pairing code
            </Button>
          </div>
        </form>
      ) : null}
    </SettingsPageShell>
  );
}

export function LearningAgentsPage() {
  const { data, isLoading, error, mutate } = useApi(settingsAgentsPages.learning, {});
  const [enabled, setEnabled] = React.useState(false);
  React.useEffect(() => {
    if (data) setEnabled(data.config.enabled);
  }, [data]);

  async function saveSettings(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!data) return;
    const fd = new FormData(e.currentTarget);
    await apiCall(agentSettings.learningSaveSettings, {
      body: {
        enabled,
        autoSkillTasks: fd.get("autoSkillTasks") === "on",
        injectTopK: Number(fd.get("injectTopK") || data.config.injectTopK || 5),
      },
    });
    await mutate();
  }

  async function add(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const text = String(fd.get("text") || "").trim();
    if (!text) return;
    await apiCall(agentSettings.learningAdd, { body: { text } });
    e.currentTarget.reset();
    await mutate();
  }

  return (
    <SettingsPageShell
      title="Learning"
      description="Excellent watches how you work and remembers what's useful. You approve each suggestion with a click."
    >
      <ErrorLine error={error} />
      {isLoading && !data ? (
        <Loading />
      ) : data ? (
        <>
          <form onSubmit={saveSettings} className="mb-6 flex flex-col gap-4">
            <SettingsSection title="Loop">
              <SettingsRow
                label="Enable learning"
                control={
                  <Switch
                    checked={enabled}
                    onCheckedChange={setEnabled}
                    aria-label="Enable learning"
                  />
                }
              />
              <CheckboxField
                name="autoSkillTasks"
                label="Auto-file skill tasks"
                defaultChecked={data.config.autoSkillTasks}
              />
              <FormField label="Inject top-K">
                <Input
                  type="number"
                  name="injectTopK"
                  defaultValue={data.config.injectTopK ?? 5}
                  className="w-24"
                />
              </FormField>
              <div>
                <Button type="submit">Save</Button>
              </div>
            </SettingsSection>
          </form>
          <SettingsSection title="What Excellent has learned" count={data.proposed.length}>
            <p className="mb-4 type-subtext text-muted-foreground">
              Reviewed {data.reviewedSessions} sessions · saved {data.savedCount} learnings
            </p>
            {data.proposed.length === 0 ? (
              <p className="type-body text-muted-foreground">Nothing waiting.</p>
            ) : (
              <ul>
                {data.proposed.map((l) => (
                  <li
                    key={l.id}
                    className="flex items-start justify-between gap-3 border-b border-border/40 py-2.5"
                  >
                    <div className="min-w-0">
                      <span className="type-body font-medium">{l.title}</span>
                      <p className="type-subtext text-muted-foreground">{l.body}</p>
                      <p className="mt-0.5 type-subtext text-muted-foreground/70">
                        from a session {formatRelativeTime(l.created_at)}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button
                        size="sm"
                        variant="accept"
                        onClick={() =>
                          void apiCall(agentSettings.learningApprove, {
                            params: { id: l.id },
                          }).then(() => mutate())
                        }
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="reject"
                        onClick={() =>
                          void apiCall(agentSettings.learningReject, { params: { id: l.id } }).then(
                            () => mutate(),
                          )
                        }
                      >
                        Not useful
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </SettingsSection>
          <SettingsSection title="Saved & active" count={data.saved.length}>
            <form onSubmit={add} className="mb-4 flex items-center gap-2">
              <Input name="text" placeholder="Remember that…" className="max-w-md" />
              <Button type="submit" variant="outline" size="sm">
                Save
              </Button>
            </form>
            {data.saved.length === 0 ? (
              <p className="type-body text-muted-foreground">Nothing saved yet.</p>
            ) : (
              <ul>
                {data.saved.map((l) => (
                  <li
                    key={l.id}
                    className="flex items-start justify-between gap-3 border-b border-border/40 py-2.5"
                  >
                    <div>
                      <span className="type-body font-medium">{l.title}</span>
                      {l.blast === "skill" ? <Badge variant="neutral">skill change</Badge> : null}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          void apiCall(agentSettings.learningPin, {
                            params: { id: l.id },
                            body: { pinned: !l.pinned },
                          }).then(() => mutate())
                        }
                      >
                        {l.pinned ? "Unpin" : "Pin"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          void apiCall(agentSettings.learningDismiss, {
                            params: { id: l.id },
                          }).then(() => mutate())
                        }
                      >
                        Dismiss
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </SettingsSection>
        </>
      ) : null}
    </SettingsPageShell>
  );
}

export function AutopilotAgentsPage() {
  const { data, isLoading, error, mutate } = useApi(settingsAgentsPages.autopilot, {});
  const [master, setMaster] = React.useState(false);
  React.useEffect(() => {
    if (data) setMaster(data.masterEnabled);
  }, [data]);

  async function save(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!data) return;
    const fd = new FormData(e.currentTarget);
    const roles: Record<string, { enabled: boolean; cadenceMinutes: number }> = {};
    for (const r of data.roles) {
      roles[r.name] = {
        enabled: fd.get(`role_${r.name}`) === "on",
        cadenceMinutes: Number(fd.get(`min_${r.name}`) || r.minutes),
      };
    }
    await apiCall(agentSettings.autopilotSaveConfig, {
      body: { enabled: master, roles },
    });
    await mutate();
  }

  return (
    <SettingsPageShell
      title="Automated review"
      description={
        <span>
          One switch turns on a standing ship → audit → fix loop. Off by default; runs cost real API
          tokens. Live activity from{" "}
          <a className="underline" href="/?env=real">
            This Mac
          </a>
          .
        </span>
      }
    >
      <ErrorLine error={error} />
      {isLoading && !data ? (
        <Loading />
      ) : data ? (
        <form onSubmit={save} className="flex flex-col gap-6">
          <SettingsSection title="Master">
            <SettingsRow
              label="Enable automated review"
              description={`Profile: ${data.profileState}`}
              control={
                <Switch
                  checked={master}
                  onCheckedChange={setMaster}
                  aria-label="Enable automated review"
                />
              }
            />
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  void apiCall(agentSettings.autopilotApplyProfile, {}).then(() => mutate())
                }
              >
                Apply review profile
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  void apiCall(agentSettings.autopilotDisable, {}).then(() => mutate())
                }
              >
                Disable
              </Button>
            </div>
          </SettingsSection>
          <SettingsSection title="Roles" count={data.roles.length}>
            {data.roles.map((r) => (
              <div
                key={r.name}
                className="flex flex-wrap items-center gap-3 border-b border-border/40 py-3"
              >
                <CheckboxField
                  name={`role_${r.name}`}
                  label={r.name}
                  description={r.description}
                  defaultChecked={r.enabled}
                />
                <Input
                  type="number"
                  name={`min_${r.name}`}
                  defaultValue={r.minutes}
                  min={data.minMinutes}
                  className="w-20"
                  aria-label={`${r.name} cadence minutes`}
                />
                {r.inProfile ? <Badge variant="neutral">profile</Badge> : null}
              </div>
            ))}
          </SettingsSection>
          <div>
            <Button type="submit">Save</Button>
          </div>
        </form>
      ) : null}
    </SettingsPageShell>
  );
}

export function ClisAgentsPage() {
  const { data, isLoading, error, mutate } = useApi(settingsAgentsPages.clis, {});
  const [cliLinkResult, setCliLinkResult] = React.useState<AgentCliLinkResult | null>(null);
  const [cliActionError, setCliActionError] = React.useState<string | null>(null);
  const [pendingCliAction, setPendingCliAction] = React.useState<string | null>(null);
  async function runCliAction(cliId: string, action: "link" | "unlink") {
    setPendingCliAction(`${action}:${cliId}`);
    setCliActionError(null);
    try {
      const result = await apiCall(
        action === "link" ? agentSettings.cliLink : agentSettings.cliUnlink,
        { params: { id: cliId } },
      );
      setCliLinkResult(result);
      await mutate();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setCliLinkResult({
        ok: false,
        cliId,
        reason: "request-failed",
        message,
      });
      setCliActionError(message);
    } finally {
      setPendingCliAction(null);
    }
  }
  return (
    <SettingsPageShell
      title="Coding Agents"
      description={
        <span>
          Choose your <strong>default coding agent</strong>, ordered fallback chain, and per-role
          overrides — then link the CLIs on this machine to Excellent. Models for harness inference
          live under{" "}
          <a className="underline" href="/settings/agents/models">
            Models
          </a>
          .
        </span>
      }
    >
      <ErrorLine error={error} />
      {isLoading && !data ? (
        <Loading />
      ) : data ? (
        <div className="flex flex-col gap-10">
          <SettingsSection title="Default agent">
            <p className="type-body text-muted-foreground mb-3">
              {data.seed
                ? `Seed: ${data.seed.defaultAgent} · chain ${data.seed.chain.join(" → ") || "—"} · ${data.seed.reason}`
                : "No seed proposed."}
              {data.seedJustWritten ? " (just written)" : ""}
            </p>
            <div className="flex flex-wrap gap-2">
              {data.agents.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center gap-2 rounded border border-border/60 px-3 py-2 type-body"
                >
                  <span className="font-medium">{a.label}</span>
                  {a.installed ? (
                    <IntentBadge intent="success">installed</IntentBadge>
                  ) : (
                    <IntentBadge intent="warning">missing</IntentBadge>
                  )}
                  {a.disabled ? <IntentBadge intent="neutral">disabled</IntentBadge> : null}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      void apiCall(agentSettings.acpSetDisabled, {
                        params: { id: a.id },
                        body: { disabled: !a.disabled },
                      }).then(() => mutate())
                    }
                  >
                    {a.disabled ? "Enable" : "Disable"}
                  </Button>
                </div>
              ))}
            </div>
          </SettingsSection>
          <SettingsSection title="Link CLIs" count={data.connected}>
            <p className="mb-4 type-body text-muted-foreground">
              Register the Excellent MCP server so each tool can read your workspace.
            </p>
            {cliLinkResult ? (
              <div
                className={`mb-4 rounded-md border p-3 type-body ${
                  cliLinkResult.ok
                    ? cliLinkResult.restartRequired
                      ? "border-amber-300 bg-amber-50 text-amber-900"
                      : "border-emerald-300 bg-emerald-50 text-emerald-900"
                    : "border-red-300 bg-red-50 text-red-900"
                }`}
                data-agent-cli-result={cliLinkResult.ok ? "ok" : "failed"}
                data-agent-cli-result-reason={cliLinkResult.reason ?? ""}
                data-agent-cli-restart-required={
                  cliLinkResult.restartRequired ? "true" : "false"
                }
                data-agent-cli-restart-cli={cliLinkResult.cliId}
              >
                <div className="font-medium">
                  {cliLinkResult.ok ? "CLI setting updated" : "CLI setting failed"}
                </div>
                <div>{cliLinkResult.message}</div>
                {cliLinkResult.reason ? (
                  <div className="mt-1 type-subtext">Reason: {cliLinkResult.reason}</div>
                ) : null}
              </div>
            ) : null}
            {cliActionError ? <ErrorLine error={cliActionError} /> : null}
            <ul>
              {data.clis.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center justify-between gap-3 border-b border-border/40 py-3"
                  data-agent-cli-row={c.id}
                  data-agent-cli-readiness={c.readinessState}
                  data-agent-cli-installed={c.installed ? "true" : "false"}
                  data-agent-cli-mcp-registered={c.mcpRegistered ? "true" : "false"}
                  data-agent-cli-restart-after-link={c.restartRequiredAfterLink ? "true" : "false"}
                >
                  <div>
                    <div className="type-body font-medium">{c.label}</div>
                    <div className="type-subtext text-muted-foreground">
                      {c.installed ? "binary ok" : "not installed"}
                      {c.mcpRegistered ? " · MCP linked" : " · MCP not linked"}
                      {c.restartRequiredAfterLink ? " · restart needed after link" : ""}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {c.installed && !c.mcpRegistered ? (
                      <Button
                        size="sm"
                        data-agent-cli-link={c.id}
                        disabled={pendingCliAction !== null}
                        onClick={() => void runCliAction(c.id, "link")}
                      >
                        {pendingCliAction === `link:${c.id}` ? "Linking…" : "Link"}
                      </Button>
                    ) : null}
                    {c.mcpRegistered ? (
                      <Button
                        size="sm"
                        variant="outline"
                        data-agent-cli-unlink={c.id}
                        disabled={pendingCliAction !== null}
                        onClick={() => void runCliAction(c.id, "unlink")}
                      >
                        {pendingCliAction === `unlink:${c.id}` ? "Unlinking…" : "Unlink"}
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </SettingsSection>
        </div>
      ) : null}
    </SettingsPageShell>
  );
}

export function OptimizationAgentsPage() {
  const { data, isLoading, error, mutate } = useApi(settingsAgentsPages.optimization, {});
  async function save(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!data) return;
    const fd = new FormData(e.currentTarget);
    const cfg = data.config as Record<string, unknown>;
    await apiCall(optimizationSettings.saveConfig, {
      body: {
        enabled: fd.get("enabled") === "on",
        budget_tokens_daily:
          Number(fd.get("budget_tokens_daily") || cfg.budget_tokens_daily || 0) || undefined,
        budget_usd_daily:
          Number(fd.get("budget_usd_daily") || cfg.budget_usd_daily || 0) || undefined,
        judge_model: String(fd.get("judge_model") || cfg.judge_model || "") || undefined,
      },
    });
    await mutate();
  }

  return (
    <SettingsPageShell
      title="Optimization"
      description={
        <span>
          The Optimization Harness meta-loop. Off by default — while off nothing is proposed or
          promoted; loops still execute.
        </span>
      }
    >
      <ErrorLine error={error} />
      {isLoading && !data ? (
        <Loading />
      ) : data ? (
        <form onSubmit={save} className="flex flex-col gap-6">
          <SettingsSection title="Harness">
            <CheckboxField
              name="enabled"
              label="Enable optimization"
              defaultChecked={Boolean(data.config.enabled)}
            />
            <FormField label="Daily token budget">
              <Input
                type="number"
                name="budget_tokens_daily"
                defaultValue={Number(data.config.budget_tokens_daily ?? 0)}
                className="w-40"
              />
            </FormField>
            <FormField label="Daily USD budget">
              <Input
                type="number"
                name="budget_usd_daily"
                defaultValue={Number(data.config.budget_usd_daily ?? 0)}
                className="w-40"
              />
            </FormField>
            <FormField label="Judge model">
              <Input
                name="judge_model"
                defaultValue={String(data.config.judge_model ?? "")}
                className="max-w-md"
              />
            </FormField>
            <p className="type-subtext text-muted-foreground">
              Today: {data.consumedTokensToday} tokens ·{" "}
              {formatUsd(data.consumedUsdToday, { dash: "cost not claimable" })}
            </p>
          </SettingsSection>
          <div>
            <Button type="submit">Save</Button>
          </div>
        </form>
      ) : null}
    </SettingsPageShell>
  );
}

export function TelemetryAgentsPage() {
  const { data, isLoading, error, mutate } = useApi(settingsAgentsPages.telemetry, {});
  async function setOptIn(optIn: boolean) {
    await apiCall(onboardingControls.setTelemetryOptIn, { body: { optIn } });
    await mutate();
  }
  return (
    <SettingsPageShell
      title="Usage analytics"
      description={
        <span>
          Excellent is local-first — your data stays on your machine. The one thing that can ever
          leave is <strong>anonymous, opt-in usage telemetry</strong>.{" "}
          <a className="underline" href="/transparency">
            Open the full transparency page →
          </a>
        </span>
      }
    >
      <ErrorLine error={error} />
      {isLoading && !data ? (
        <Loading />
      ) : data ? (
        <div className="space-y-6">
          <SettingsSection title="Consent">
            <SettingsRow
              label="Share anonymous usage analytics"
              description={data.optedIn ? "Currently opted in." : "Currently opted out."}
              control={
                <Switch
                  checked={data.optedIn}
                  onCheckedChange={(v) => void setOptIn(v)}
                  aria-label="Share anonymous usage analytics"
                />
              }
            />
          </SettingsSection>
          <SettingsSection title="Sink">
            <p className="type-body text-muted-foreground">
              Provider: {data.sinkProvider}
              {data.sinkConfigured ? " · configured" : " · not configured"}
              {data.sinkDestination ? ` · ${data.sinkDestination}` : ""}
            </p>
          </SettingsSection>
        </div>
      ) : null}
    </SettingsPageShell>
  );
}

export function VerdictsAgentsPage() {
  const { data, isLoading, error, mutate } = useApi(settingsAgentsPages.verdicts, {});
  async function save(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!data) return;
    const fd = new FormData(e.currentTarget);
    const routing: Record<string, string | null> = {};
    for (const dept of data.departments ?? Object.keys(data.routing)) {
      const v = String(fd.get(`dept_${dept}`) || "");
      routing[dept] = v || null;
    }
    await apiCall(verdicts.setRouting, { body: { routing } });
    await mutate();
  }
  return (
    <SettingsPageShell
      title="Review routing"
      description={
        <span>
          Assign each department of concern to the person who should approve and annotate its items.
          Unassigned departments fall to you ({data?.currentLabel ?? "You"}).
        </span>
      }
    >
      <ErrorLine error={error} />
      {isLoading && !data ? (
        <Loading />
      ) : data ? (
        <form onSubmit={save} className="flex flex-col gap-4">
          <SettingsSection title="Departments">
            {(data.departments ?? Object.keys(data.routing)).map((dept) => (
              <FormField key={dept} label={dept}>
                <Select name={`dept_${dept}`} defaultValue={data.routing[dept] ?? ""}>
                  <SelectTrigger className="w-full max-w-md">
                    <SelectValue placeholder="Unassigned (you)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Unassigned ({data.currentLabel})</SelectItem>
                    {data.approvers.map((a) => (
                      <SelectItem key={a.external_id} value={a.external_id}>
                        {a.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            ))}
          </SettingsSection>
          <div>
            <Button type="submit">Save</Button>
          </div>
        </form>
      ) : null}
    </SettingsPageShell>
  );
}

export function MonitorAgentsPage() {
  const { data, isLoading, error } = useApi(settingsAgentsPages.monitor, {});
  return (
    <SettingsPageShell
      title="Distribution monitor"
      description={
        <span>
          Computer use, not a closed API: Excellent drives <strong>your own browser</strong> on{" "}
          <strong>your own machine</strong> through public threads and queues the best openings into{" "}
          <a className="underline" href="/play">
            Play
          </a>
          .
        </span>
      }
    >
      <ErrorLine error={error} />
      {isLoading && !data ? (
        <Loading />
      ) : data ? (
        <div className="flex flex-col gap-6">
          <SettingsSection title="Status">
            <p className="type-body">
              Monitor is <strong>{data.enabled ? "enabled" : "disabled"}</strong>. Browser:{" "}
              {data.status.browser ?? "—"}
              {data.status.running ? " (running)" : ""}.
            </p>
            {data.suggestedKeywords.length ? (
              <p className="mt-2 type-subtext text-muted-foreground">
                Suggested keywords: {data.suggestedKeywords.join(", ")}
              </p>
            ) : null}
            <p className="mt-2 type-subtext text-muted-foreground">
              {data.opportunities.length} queued opportunities.
            </p>
          </SettingsSection>
        </div>
      ) : null}
    </SettingsPageShell>
  );
}

export function IntegrateAgentsPage() {
  const { data, isLoading, error } = useApi(settingsAgentsPages.integrate, {});
  return (
    <SettingsPageShell
      title="Self-integration"
      description={
        <span>
          Computer use, pointed at your integrations: Excellent scans <strong>this machine</strong>{" "}
          for the tools you already use, then — once you <strong>allow</strong> — drives{" "}
          <strong>your own browser</strong> to complete connections. Manage connections in{" "}
          <a className="underline" href="/settings/integrations/connections">
            Integrations
          </a>
          .
        </span>
      }
    >
      <ErrorLine error={error} />
      {isLoading && !data ? (
        <Loading />
      ) : data ? (
        <SettingsSection title="Status">
          <p className="type-body">
            Self-integration is <strong>{data.enabled ? "enabled" : "disabled"}</strong>
            {data.acknowledged ? " · acknowledged" : " · not yet acknowledged"}.
          </p>
          <p className="mt-2 type-subtext text-muted-foreground">
            Browser: {String(data.status.browser ?? "—")}
            {data.extensionId ? ` · extension ${data.extensionId}` : ""}
          </p>
        </SettingsSection>
      ) : null}
    </SettingsPageShell>
  );
}
